
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';

interface JoinTripModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const JoinTripModal: React.FC<JoinTripModalProps> = ({ open, onOpenChange }) => {
  const [tripCode, setTripCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleJoinTrip = async () => {
    if (!tripCode.trim()) {
      toast({
        title: "Trip code required",
        description: "Please enter a trip code to join",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Check if trip exists with the given code
      const { data: tripData, error: tripError } = await supabase
        .from('trips')
        .select('id, name')
        .eq('code', tripCode.trim())
        .single();

      if (tripError) {
        toast({
          title: "Trip not found",
          description: "The trip code you entered doesn't exist. Please check and try again.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // If user is not signed in, redirect to auth page
      if (!user) {
        // Store trip code in session storage for later use after sign-up
        sessionStorage.setItem('pendingTripCode', tripCode.trim());
        sessionStorage.setItem('pendingTripId', tripData.id);
        sessionStorage.setItem('pendingTripName', tripData.name);
        
        // Close the modal and redirect to auth page
        onOpenChange(false);
        navigate('/auth');
        return;
      }
      
      // User is signed in, proceed with joining the trip
      joinExistingTrip(tripData.id, tripData.name);
    } catch (error) {
      toast({
        title: "An error occurred",
        description: "Please try again later",
        variant: "destructive"
      });
      console.error("Error joining trip:", error);
    } finally {
      setLoading(false);
    }
  };

  const joinExistingTrip = async (tripId: string, tripName: string) => {
    try {
      // Check if user is already a member of this trip
      const { data: existingMembership, error: membershipCheckError } = await supabase
        .from('trip_members')
        .select('id')
        .eq('trip_id', tripId)
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (membershipCheckError) {
        console.error("Error checking membership:", membershipCheckError);
      }
      
      // If not already a member, add to trip_members
      if (!existingMembership) {
        const { error: memberError } = await supabase
          .from('trip_members')
          .insert({
            trip_id: tripId,
            user_id: user.id
          });

        if (memberError) {
          toast({
            title: "Error joining trip",
            description: memberError.message,
            variant: "destructive"
          });
          return;
        }
      }

      toast({
        title: "Success!",
        description: `You've joined "${tripName}"!`,
      });
      
      onOpenChange(false);
      navigate(`/trips/${tripId}/itinerary`);
    } catch (error) {
      toast({
        title: "An error occurred",
        description: "Please try again later",
        variant: "destructive"
      });
      console.error("Error finalizing trip join:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Join a Trip</DialogTitle>
          <DialogDescription>
            Enter the trip code shared with you to join an existing trip.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2 py-4">
          <Input
            placeholder="Enter trip code..."
            value={tripCode}
            onChange={(e) => setTripCode(e.target.value)}
            className="flex-1"
          />
        </div>
        <DialogFooter className="flex space-x-2 justify-end">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleJoinTrip}
            className="bg-glowingTeal hover:bg-glowingTeal/90"
            disabled={loading}
          >
            {loading ? 'Joining...' : 'Join Trip'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JoinTripModal;
